
import Vue, { PropType } from 'vue';
import { useStore as useShapeStore } from '@/stores/v2/shape';
import { useStore as useProductStore } from '@/stores/v2/product';
import { useStore as useHistoryStore } from '@/stores/v2/history';
import { useStore as useNotificationStore } from '@/stores/v2/notification';
import { useStore as useColorManagementStore } from '@/stores/v2/colorsManagement';
import { mapActions, mapState, mapStores } from 'pinia';
import { CvButton } from '@carbon/vue';
import Close16 from '@carbon/icons-vue/es/close/16';
import { generateEventPayload } from '@/utils/generatePriceParamsEventPayload';
import { ProductPrintPositionAddModel } from '@/typings';
import { ColorDropdown } from '@/web-components/import-wizard-v2/components/ColorManagement/ColorDropdown';
import { createStateSnapshot } from '@/utils/historyHelper';
import { PmsColor } from '@midocean/webapps-shared-domain';
import { TextShape } from '@/web-components/import-wizard/typings/pintura';

export default Vue.extend({
  name: 'ColorManagement',
  components: {
    CvButton,
    Close16,
    ColorDropdown,
  },
  props: {
    isDisabled: {
      type: Boolean,
      required: false,
    },
  },
  computed: {
    ...mapStores(useShapeStore, useColorManagementStore),
    ...mapState(useProductStore, [
      'data',
      'isLaserConverter',
      'isDebossingTechnique',
      'unavailablePrintPositions',
      'selectedVariant',
      'selectedPrintPosition',
      'isFullColor',
    ]),
    ...mapState(useColorManagementStore, ['availablePmsColors']),
    ...mapState(useShapeStore, ['shapes', 'positionShapeMap']),
    allPmsColors(): PmsColor[] {
      const detected = this.detectedPmsColors;
      const availableWithoutDetected = this.availablePmsColors.filter((c) => {
        const index = detected.findIndex((dc) => dc.name === c.name);
        return index === -1;
      });
      // show detected colors first, then the rest of available pms colors
      const mergedList = detected.concat(availableWithoutDetected);

      return mergedList;
    },
    detectedPmsColors(): PmsColor[] {
      const textPrintPositionShapes = this.shapes
        .filter((shape) =>
          this.positionShapeMap.some(
            (psm) =>
              psm.id === this.selectedPrintPosition.id &&
              psm.logoId === shape.id
          )
        )
        .filter((shape) => shape.kind === 'Text') as TextShape[];

      const textPmsColors =
        this.availablePmsColors.length > 0
          ? textPrintPositionShapes.map((text) => {
              return this.availablePmsColors.find(
                (c) => c.name === text.w2pText.colorName
              ) as PmsColor;
            })
          : [];

      // All the colours on the current print position
      const allPmsColors = [...textPmsColors];

      const uniquePmsColors = [
        ...new Map(allPmsColors.map((item) => [item['name'], item])).values(),
      ];

      const array = uniquePmsColors.map((color: PmsColor) => {
        return {
          ...color,
          name: color.name.includes('Closest Pantone: ')
            ? color.name.slice(17)
            : color.name,
        };
      });

      return [
        ...new Map(
          array.map((item: PmsColor) => [item['name'], item])
        ).values(),
      ] as PmsColor[];
    },
  },

  methods: {
    ...mapActions(useColorManagementStore, ['getPmsColorsOnPrintPosition']),
    ...mapActions(useShapeStore, ['replaceColor', 'removeColor']),
    ...mapActions(useHistoryStore, ['recordHistory']),
    ...mapActions(useNotificationStore, ['runValidators']),

    async replaceWith(index: number, color: string) {
      if (this.getPmsColorsOnPrintPosition()[index].name === color) {
        // if the same color is selected, do nothing.
        return;
      }

      const historyStore = useHistoryStore();

      const selectedPmsColor =
        this.availablePmsColors?.find((c) => {
          return c.name === color;
        }) || ({} as PmsColor);

      await this.replaceColor(
        this.getPmsColorsOnPrintPosition()[index],
        selectedPmsColor
      );

      const eventData = this.unavailablePrintPositions.filter(
        (u) => u.positionId === this.selectedPrintPosition.id
      ) as ProductPrintPositionAddModel[];

      const eventPayload = generateEventPayload();

      this.$emit('wizard:price-params-changed', {
        productPrintPositions: eventPayload,
      });

      this.$emit('action:change-color', eventData);

      const snapshot = createStateSnapshot();
      historyStore.recordHistory(snapshot);

      this.runValidators();
    },

    async colorToRemove(color: PmsColor) {
      await this.removeColor(color);

      const eventData = this.unavailablePrintPositions.filter(
        (u) => u.positionId === this.selectedPrintPosition.id
      ) as ProductPrintPositionAddModel[];

      this.$emit('action:change-color', eventData);

      const eventPayload = generateEventPayload();

      this.$emit('wizard:price-params-changed', {
        productPrintPositions: eventPayload,
      });

      const snapshot = createStateSnapshot();
      this.recordHistory(snapshot);

      this.runValidators();
    },
  },
});
